import React, { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase/config';
import { useAuth } from '../context/AuthContext';
import { LogIn, Eye, EyeOff, AlertCircle, UserPlus } from 'lucide-react';
import toast from 'react-hot-toast';

const ADMIN_EMAIL = 'admin@metamax.com';
const ADMIN_PASSWORD = 'MetaMax@2024';

export default function Login() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [email, setEmail] = useState(ADMIN_EMAIL);
  const [password, setPassword] = useState(ADMIN_PASSWORD);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  // Already logged in — send to where they came from or dashboard
  const from = location.state?.from?.pathname || '/dashboard';
  if (user) return <Navigate to={from} replace />;

  // Still resolving auth state
  if (user === undefined) {
    return (
      <div className="min-h-screen bg-[#080E1E] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#2D5BE3] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      toast.success('Welcome back!');
      navigate(from, { replace: true });
    } catch (err) {
      const msg =
        err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password'
          ? 'Invalid email or password.'
          : err.code === 'auth/user-not-found'
          ? 'No account found with this email.'
          : err.code === 'auth/too-many-requests'
          ? 'Too many attempts. Try again later.'
          : 'Sign in failed. Check your credentials.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAdmin = async () => {
    setError('');
    setCreating(true);
    try {
      await createUserWithEmailAndPassword(auth, ADMIN_EMAIL, ADMIN_PASSWORD);
      toast.success('Admin account created! Signing you in…');
      await signInWithEmailAndPassword(auth, ADMIN_EMAIL, ADMIN_PASSWORD);
      navigate(from, { replace: true });
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        setError('Admin account already exists. Just sign in.');
      } else {
        setError('Could not create admin: ' + err.message);
      }
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#080E1E] flex items-center justify-center p-4">
      {/* Background glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[600px]
          bg-[#2D5BE3]/10 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-sm relative">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <img
            src="/metamax-logo.png"
            alt="MetaMax"
            className="w-20 h-20 object-contain mb-4"
          />
          <h1 className="text-white font-bold text-2xl tracking-widest">META MAX</h1>
          <p className="text-[#D4AF37] text-sm font-medium mt-1">TRAVEL BEYOND LIMITS</p>
          <p className="text-[#546E7A] text-xs mt-2">Admin Panel</p>
        </div>

        {/* Card */}
        <div className="bg-[#111827] border border-[#1E2D45] rounded-2xl p-8">
          <h2 className="text-white font-semibold text-lg mb-1">Sign in</h2>
          <p className="text-[#546E7A] text-sm mb-6">Enter your admin credentials to continue.</p>

          {error && (
            <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30
              rounded-xl px-4 py-3 mb-5">
              <AlertCircle size={16} className="text-red-400 flex-shrink-0" />
              <span className="text-red-400 text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-[#B0BEC5] text-xs font-medium mb-1.5">
                Email address
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="admin@metamax.com"
                className="w-full bg-[#1A2235] border border-[#1E2D45] rounded-xl px-4 py-3
                  text-white text-sm placeholder-[#546E7A] outline-none
                  focus:border-[#2D5BE3] transition-colors"
              />
            </div>

            <div>
              <label className="block text-[#B0BEC5] text-xs font-medium mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  placeholder="••••••••"
                  className="w-full bg-[#1A2235] border border-[#1E2D45] rounded-xl px-4 py-3 pr-11
                    text-white text-sm placeholder-[#546E7A] outline-none
                    focus:border-[#2D5BE3] transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#546E7A]
                    hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-[#2D5BE3] hover:bg-[#2450cc]
                disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold
                rounded-xl py-3 text-sm transition-colors mt-2"
            >
              {loading
                ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                : <><LogIn size={16} /> Sign in</>
              }
            </button>
          </form>
        </div>

        <p className="text-center text-[#546E7A] text-xs mt-6">
          MetaMax Admin Panel · Restricted access
        </p>

        {/* One-time setup — creates the default admin account */}
       
      </div>
    </div>
  );
}
